import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const FAQPage = () => {
  // Estado para controlar quais perguntas estão expandidas
  const [expandedItems, setExpandedItems] = useState({});

  // Função para alternar o estado de expansão de uma pergunta
  const toggleItem = (id) => {
    setExpandedItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#1A3A5F] to-[#4D8BC9] text-white">
        <div className="container mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Perguntas Frequentes</h1>
            <p className="text-xl mb-8">
              Respostas para as dúvidas mais comuns sobre nossos serviços e soluções
            </p>
          </motion.div>
        </div>
      </section>

      {/* Introdução */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4 text-[#1A3A5F]">Como Podemos Ajudar?</h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              Reunimos as perguntas mais frequentes sobre nossos serviços, processos e soluções. 
              Se você não encontrar a resposta que procura, não hesite em entrar em contato conosco.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Categorias de FAQ */}
      <section className="py-8">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
            {/* Categoria 1 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-white p-6 rounded-lg shadow-md text-center"
            >
              <div className="w-12 h-12 bg-[#1A3A5F] rounded-full flex items-center justify-center mb-4 mx-auto">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold mb-2 text-[#1A3A5F]">Serviços</h3>
              <p className="text-gray-700 mb-4">
                Dúvidas sobre nossos serviços, metodologias e processos
              </p>
              <a 
                href="#servicos" 
                className="inline-block text-[#1A3A5F] hover:text-[#4D8BC9] font-bold transition-colors"
              >
                Ver perguntas →
              </a>
            </motion.div>

            {/* Categoria 2 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-white p-6 rounded-lg shadow-md text-center"
            >
              <div className="w-12 h-12 bg-[#4D8BC9] rounded-full flex items-center justify-center mb-4 mx-auto">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
              </div>
              <h3 className="text-lg font-bold mb-2 text-[#4D8BC9]">Normativas</h3>
              <p className="text-gray-700 mb-4">
                Dúvidas sobre normas técnicas, regulamentos e legislação
              </p>
              <a 
                href="#normativas" 
                className="inline-block text-[#4D8BC9] hover:text-[#1A3A5F] font-bold transition-colors"
              >
                Ver perguntas →
              </a>
            </motion.div>

            {/* Categoria 3 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white p-6 rounded-lg shadow-md text-center"
            >
              <div className="w-12 h-12 bg-[#1A3A5F] rounded-full flex items-center justify-center mb-4 mx-auto">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold mb-2 text-[#1A3A5F]">Contratação</h3>
              <p className="text-gray-700 mb-4">
                Dúvidas sobre processos de contratação, prazos e investimentos
              </p>
              <a 
                href="#contratacao" 
                className="inline-block text-[#1A3A5F] hover:text-[#4D8BC9] font-bold transition-colors"
              >
                Ver perguntas →
              </a>
            </motion.div>

            {/* Categoria 4 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
              className="bg-white p-6 rounded-lg shadow-md text-center"
            >
              <div className="w-12 h-12 bg-[#4D8BC9] rounded-full flex items-center justify-center mb-4 mx-auto">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold mb-2 text-[#4D8BC9]">Contato</h3>
              <p className="text-gray-700 mb-4">
                Dúvidas sobre como entrar em contato, localização e atendimento
              </p>
              <a 
                href="#contato" 
                className="inline-block text-[#4D8BC9] hover:text-[#1A3A5F] font-bold transition-colors"
              >
                Ver perguntas →
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Perguntas e Respostas - Serviços */}
      <section id="servicos" className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="mb-8"
          >
            <h2 className="text-2xl font-bold mb-4 text-[#1A3A5F]">Perguntas sobre Serviços</h2>
          </motion.div>

          <div className="space-y-4 max-w-4xl">
            {/* Pergunta 1 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-white rounded-lg shadow-md overflow-hidden"
            >
              <div 
                className="p-4 flex justify-between items-center cursor-pointer"
                onClick={() => toggleItem('servicos-1')}
              >
                <h3 className="text-lg font-bold text-[#1A3A5F]">Quais são os principais serviços oferecidos pelo Grupo M7 CORP?</h3>
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className={`h-6 w-6 text-[#4D8BC9] transition-transform ${expandedItems['servicos-1'] ? 'transform rotate-180' : ''}`} 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              {expandedItems['servicos-1'] && (
                <div className="p-4 pt-0 border-t border-gray-200">
                  <p className="text-gray-700">
                    O Grupo M7 CORP, através de suas empresas MAIN'TECH PLUS e IADG EXPERTS, oferece uma ampla gama de serviços, incluindo:
                  </p>
                  <ul className="list-disc pl-5 mt-2 space-y-1 text-gray-700">
                    <li>Inspeção e ensaios não destrutivos</li>
                    <li>Análise de integridade estrutural</li>
                    <li>Cálculos de vida remanescente</li>
                    <li>Adequação às normas regulamentadoras (NR-11, NR-13, NR-20, NR-33, NR-35)</li>
                    <li>Projetos de engenharia</li>
                    <li>Manutenção industrial</li>
                    <li>Consultoria técnica</li>
                    <li>Treinamentos especializados</li>
                  </ul>
                  <p className="mt-3 text-gray-700">
                    Para mais detalhes sobre cada serviço, visite nossa <Link to="/servicos" className="text-[#4D8BC9] hover:text-[#1A3A5F]">página de serviços</Link>.
                  </p>
                </div>
              )}
            </motion.div>

            {/* Pergunta 2 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-white rounded-lg shadow-md overflow-hidden"
            >
              <div 
                className="p-4 flex justify-between items-center cursor-pointer"
                onClick={() => toggleItem('servicos-2')}
              >
                <h3 className="text-lg font-bold text-[#1A3A5F]">Quais tipos de equipamentos o Grupo M7 CORP inspeciona?</h3>
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className={`h-6 w-6 text-[#4D8BC9] transition-transform ${expandedItems['servicos-2'] ? 'transform rotate-180' : ''}`} 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              {expandedItems['servicos-2'] && (
                <div className="p-4 pt-0 border-t border-gray-200">
                  <p className="text-gray-700">
                    Nossa equipe técnica está qualificada para inspecionar uma ampla variedade de equipamentos industriais, incluindo:
                  </p>
                  <ul className="list-disc pl-5 mt-2 space-y-1 text-gray-700">
                    <li>Vasos de pressão</li>
                    <li>Caldeiras</li>
                    <li>Tubulações industriais</li>
                    <li>Tanques de armazenamento</li>
                    <li>Estruturas metálicas</li>
                    <li>Equipamentos de elevação e transporte (pontes rolantes, guindastes, talhas)</li>
                    <li>Sistemas de proteção contra incêndio</li>
                    <li>Sistemas de refrigeração industrial</li>
                    <li>Trocadores de calor</li>
                    <li>Reatores químicos</li>
                  </ul>
                  <p className="mt-3 text-gray-700">
                    Todos os nossos serviços de inspeção são realizados por profissionais certificados, seguindo normas técnicas nacionais e internacionais.
                  </p>
                </div>
              )}
            </motion.div>

            {/* Pergunta 3 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white rounded-lg shadow-md overflow-hidden"
            >
              <div 
                className="p-4 flex justify-between items-center cursor-pointer"
                onClick={() => toggleItem('servicos-3')}
              >
                <h3 className="text-lg font-bold text-[#1A3A5F]">Quais métodos de ensaios não destrutivos são utilizados?</h3>
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className={`h-6 w-6 text-[#4D8BC9] transition-transform ${expandedItems['servicos-3'] ? 'transform rotate-180' : ''}`} 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              {expandedItems['servicos-3'] && (
                <div className="p-4 pt-0 border-t border-gray-200">
                  <p className="text-gray-700">
                    Utilizamos diversos métodos de ensaios não destrutivos (END), incluindo:
                  </p>
                  <ul className="list-disc pl-5 mt-2 space-y-1 text-gray-700">
                    <li>Ultrassom convencional e avançado (Phased Array, TOFD)</li>
                    <li>Radiografia industrial</li>
                    <li>Líquido penetrante</li>
                    <li>Partículas magnéticas</li>
                    <li>Inspeção visual</li>
                    <li>Termografia</li>
                    <li>Correntes parasitas</li>
                    <li>Emissão acústica</li>
                    <li>Medição de espessura por ultrassom</li>
                    <li>Análise de vibrações</li>
                  </ul>
                  <p className="mt-3 text-gray-700">
                    Nossos inspetores são certificados pelo SNQC/ABENDI, garantindo a qualidade e confiabilidade dos ensaios realizados.
                  </p>
                </div>
              )}
            </motion.div>

            {/* Pergunta 4 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
              className="bg-white rounded-lg shadow-md overflow-hidden"
            >
              <div 
                className="p-4 flex justify-between items-center cursor-pointer"
                onClick={() => toggleItem('servicos-4')}
              >
                <h3 className="text-lg font-bold text-[#1A3A5F]">Como é realizada a análise de integridade estrutural?</h3>
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className={`h-6 w-6 text-[#4D8BC9] transition-transform ${expandedItems['servicos-4'] ? 'transform rotate-180' : ''}`} 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              {expandedItems['servicos-4'] && (
                <div className="p-4 pt-0 border-t border-gray-200">
                  <p className="text-gray-700">
                    A análise de integridade estrutural é um processo abrangente que envolve várias etapas:
                  </p>
                  <ol className="list-decimal pl-5 mt-2 space-y-1 text-gray-700">
                    <li>Levantamento de dados históricos do equipamento</li>
                    <li>Inspeção visual e dimensional</li>
                    <li>Realização de ensaios não destrutivos</li>
                    <li>Análise de materiais e possíveis mecanismos de degradação</li>
                    <li>Cálculos estruturais conforme códigos aplicáveis (ASME, API, etc.)</li>
                    <li>Análise de elementos finitos (quando necessário)</li>
                    <li>Avaliação de vida remanescente</li>
                    <li>Elaboração de relatório técnico com conclusões e recomendações</li>
                  </ol>
                  <p className="mt-3 text-gray-700">
                    O objetivo principal é determinar a condição atual do equipamento, identificar possíveis problemas e estimar sua vida útil remanescente, garantindo a operação segura e confiável.
                  </p>
                </div>
              )}
            </motion.div>

            {/* Pergunta 5 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              viewport={{ once: true }}
              className="bg-white rounded-lg shadow-md overflow-hidden"
            >
              <div 
                className="p-4 flex justify-between items-center cursor-pointer"
                onClick={() => toggleItem('servicos-5')}
              >
                <h3 className="text-lg font-bold text-[#1A3A5F]">O Grupo M7 CORP oferece serviços de treinamento?</h3>
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className={`h-6 w-6 text-[#4D8BC9] transition-transform ${expandedItems['servicos-5'] ? 'transform rotate-180' : ''}`} 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              {expandedItems['servicos-5'] && (
                <div className="p-4 pt-0 border-t border-gray-200">
                  <p className="text-gray-700">
                    Sim, oferecemos diversos treinamentos especializados para profissionais da indústria, incluindo:
                  </p>
                  <ul className="list-disc pl-5 mt-2 space-y-1 text-gray-700">
                    <li>Treinamentos sobre normas regulamentadoras (NR-11, NR-13, NR-20, NR-33, NR-35)</li>
                    <li>Cursos de inspeção de equipamentos</li>
                    <li>Capacitação em ensaios não destrutivos</li>
                    <li>Treinamentos sobre análise de integridade</li>
                    <li>Cursos sobre gestão de manutenção</li>
                    <li>Capacitação em segurança industrial</li>
                  </ul>
                  <p className="mt-3 text-gray-700">
                    Nossos treinamentos podem ser realizados in company ou em nossas instalações, com conteúdo personalizado de acordo com as necessidades específicas de cada cliente. Para mais informações, entre em contato conosco através da <Link to="/contato" className="text-[#4D8BC9] hover:text-[#1A3A5F]">página de contato</Link>.
                  </p>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Perguntas e Respostas - Normativas */}
      <section id="normativas" className="py-12">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="mb-8"
          >
            <h2 className="text-2xl font-bold mb-4 text-[#1A3A5F]">Perguntas sobre Normativas</h2>
          </motion.div>

          <div className="space-y-4 max-w-4xl">
            {/* Pergunta 1 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-white rounded-lg shadow-md overflow-hidden"
            >
              <div 
                className="p-4 flex justify-between items-center cursor-pointer"
                onClick={() => toggleItem('normativas-1')}
              >
                <h3 className="text-lg font-bold text-[#1A3A5F]">O que é a NR-13 e quais equipamentos estão sujeitos a ela?</h3>
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className={`h-6 w-6 text-[#4D8BC9] transition-transform ${expandedItems['normativas-1'] ? 'transform rotate-180' : ''}`} 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              {expandedItems['normativas-1'] && (
                <div className="p-4 pt-0 border-t border-gray-200">
                  <p className="text-gray-700">
                    A NR-13 (Norma Regulamentadora 13) é uma norma do Ministério do Trabalho que estabelece requisitos mínimos para gestão da integridade estrutural de caldeiras, vasos de pressão e tubulações, visando a segurança e saúde dos trabalhadores.
                  </p>
                  <p className="mt-2 text-gray-700">
                    Os equipamentos sujeitos à NR-13 incluem:
                  </p>
                  <ul className="list-disc pl-5 mt-2 space-y-1 text-gray-700">
                    <li>Caldeiras (equipamentos destinados a produzir vapor sob pressão superior à atmosférica)</li>
                    <li>Vasos de pressão (equipamentos que contêm fluidos sob pressão interna ou externa, diferentes das condições atmosféricas)</li>
                    <li>Tubulações (conjunto de linhas, incluindo seus acessórios, destinadas ao transporte de fluidos)</li>
                    <li>Tanques metálicos de armazenamento (em determinadas condições)</li>
                  </ul>
                  <p className="mt-3 text-gray-700">
                    A NR-13 estabelece requisitos para inspeção inicial, periódica e extraordinária, bem como para documentação, treinamento de operadores e plano de manutenção desses equipamentos.
                  </p>
                  <p className="mt-2 text-gray-700">
                    Para mais informações sobre a NR-13, visite nossa <Link to="/normativas/nr13" className="text-[#4D8BC9] hover:text-[#1A3A5F]">página específica sobre a NR-13</Link>.
                  </p>
                </div>
              )}
            </motion.div>

            {/* Pergunta 2 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-white rounded-lg shadow-md overflow-hidden"
            >
              <div 
                className="p-4 flex justify-between items-center cursor-pointer"
                onClick={() => toggleItem('normativas-2')}
              >
                <h3 className="text-lg font-bold text-[#1A3A5F]">Qual a periodicidade das inspeções exigidas pela NR-13?</h3>
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className={`h-6 w-6 text-[#4D8BC9] transition-transform ${expandedItems['normativas-2'] ? 'transform rotate-180' : ''}`} 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              {expandedItems['normativas-2'] && (
                <div className="p-4 pt-0 border-t border-gray-200">
                  <p className="text-gray-700">
                    A periodicidade das inspeções varia de acordo com o tipo de equipamento, categoria e condições operacionais:
                  </p>
                  <p className="mt-2 font-semibold text-gray-700">Para caldeiras:</p>
                  <ul className="list-disc pl-5 mt-1 space-y-1 text-gray-700">
                    <li>Inspeção de segurança periódica: 12 meses para caldeiras das categorias A e B</li>
                    <li>Teste hidrostático: a critério do Profissional Habilitado, respeitando o intervalo máximo de 9 anos para caldeiras de categorias A e B</li>
                  </ul>
                  <p className="mt-2 font-semibold text-gray-700">Para vasos de pressão:</p>
                  <ul className="list-disc pl-5 mt-1 space-y-1 text-gray-700">
                    <li>Categoria I: 3 anos</li>
                    <li>Categoria II: 5 anos</li>
                    <li>Categoria III: 6 anos</li>
                    <li>Categoria IV: 8 anos</li>
                    <li>Categoria V: 10 anos</li>
                  </ul>
                  <p className="mt-2 text-gray-700">
                    Estes prazos podem ser reduzidos dependendo das condições operacionais, ambiente corrosivo ou resultados de inspeções anteriores. O Profissional Habilitado pode estabelecer prazos diferentes com base em sua avaliação técnica.
                  </p>
                  <p className="mt-2 text-gray-700">
                    Além das inspeções periódicas, são exigidas inspeções iniciais (antes da entrada em operação) e extraordinárias (após alterações, reparos, acidentes ou quando houver indícios de falhas).
                  </p>
                </div>
              )}
            </motion.div>

            {/* Pergunta 3 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white rounded-lg shadow-md overflow-hidden"
            >
              <div 
                className="p-4 flex justify-between items-center cursor-pointer"
                onClick={() => toggleItem('normativas-3')}
              >
                <h3 className="text-lg font-bold text-[#1A3A5F]">O que é a NR-11 e quais equipamentos estão sujeitos a ela?</h3>
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className={`h-6 w-6 text-[#4D8BC9] transition-transform ${expandedItems['normativas-3'] ? 'transform rotate-180' : ''}`} 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              {expandedItems['normativas-3'] && (
                <div className="p-4 pt-0 border-t border-gray-200">
                  <p className="text-gray-700">
                    A NR-11 (Norma Regulamentadora 11) estabelece os requisitos de segurança para operação de equipamentos de transporte, movimentação, armazenagem e manuseio de materiais.
                  </p>
                  <p className="mt-2 text-gray-700">
                    Os equipamentos sujeitos à NR-11 incluem:
                  </p>
                  <ul className="list-disc pl-5 mt-2 space-y-1 text-gray-700">
                    <li>Pontes rolantes</li>
                    <li>Guindastes</li>
                    <li>Talhas</li>
                    <li>Empilhadeiras</li>
                    <li>Elevadores de carga</li>
                    <li>Transportadores contínuos</li>
                    <li>Sistemas de armazenagem (estantes, porta-paletes)</li>
                    <li>Equipamentos de içamento e movimentação de cargas em geral</li>
                  </ul>
                  <p className="mt-3 text-gray-700">
                    A NR-11 estabelece requisitos para inspeção, operação segura, capacitação de operadores e manutenção desses equipamentos, visando prevenir acidentes e garantir a segurança dos trabalhadores.
                  </p>
                  <p className="mt-2 text-gray-700">
                    Para mais informações sobre a NR-11, visite nossa <Link to="/normativas/nr11" className="text-[#4D8BC9] hover:text-[#1A3A5F]">página específica sobre a NR-11</Link>.
                  </p>
                </div>
              )}
            </motion.div>

            {/* Pergunta 4 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
              className="bg-white rounded-lg shadow-md overflow-hidden"
            >
              <div 
                className="p-4 flex justify-between items-center cursor-pointer"
                onClick={() => toggleItem('normativas-4')}
              >
                <h3 className="text-lg font-bold text-[#1A3A5F]">Quais são as principais normas técnicas aplicáveis a equipamentos industriais?</h3>
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className={`h-6 w-6 text-[#4D8BC9] transition-transform ${expandedItems['normativas-4'] ? 'transform rotate-180' : ''}`} 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              {expandedItems['normativas-4'] && (
                <div className="p-4 pt-0 border-t border-gray-200">
                  <p className="text-gray-700">
                    Além das Normas Regulamentadoras (NRs) do Ministério do Trabalho, existem diversas normas técnicas nacionais e internacionais aplicáveis a equipamentos industriais:
                  </p>
                  <p className="mt-2 font-semibold text-gray-700">Normas Nacionais (ABNT):</p>
                  <ul className="list-disc pl-5 mt-1 space-y-1 text-gray-700">
                    <li>ABNT NBR 16035 - Caldeiras e vasos de pressão</li>
                    <li>ABNT NBR 13598 - Vasos de pressão para gases</li>
                    <li>ABNT NBR 15417 - Vasos de pressão - Inspeção de segurança em serviço</li>
                    <li>ABNT NBR 14105 - Adequação de caldeiras e vasos de pressão</li>
                    <li>ABNT NBR 15420 - Inspeção de caldeiras</li>
                  </ul>
                  <p className="mt-2 font-semibold text-gray-700">Normas Internacionais:</p>
                  <ul className="list-disc pl-5 mt-1 space-y-1 text-gray-700">
                    <li>ASME BPVC - Código de Caldeiras e Vasos de Pressão</li>
                    <li>API 510 - Inspeção de vasos de pressão</li>
                    <li>API 570 - Inspeção de tubulações</li>
                    <li>API 653 - Inspeção de tanques</li>
                    <li>API 579/ASME FFS-1 - Fitness for Service</li>
                    <li>AWS D1.1 - Código de Soldagem Estrutural</li>
                    <li>ISO 9712 - Qualificação e certificação de pessoal de END</li>
                  </ul>
                  <p className="mt-3 text-gray-700">
                    Nossa equipe técnica está capacitada para realizar serviços em conformidade com estas e outras normas técnicas aplicáveis, garantindo a segurança e confiabilidade dos equipamentos.
                  </p>
                </div>
              )}
            </motion.div>

            {/* Pergunta 5 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              viewport={{ once: true }}
              className="bg-white rounded-lg shadow-md overflow-hidden"
            >
              <div 
                className="p-4 flex justify-between items-center cursor-pointer"
                onClick={() => toggleItem('normativas-5')}
              >
                <h3 className="text-lg font-bold text-[#1A3A5F]">Como o Grupo M7 CORP pode ajudar na adequação às normas regulamentadoras?</h3>
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className={`h-6 w-6 text-[#4D8BC9] transition-transform ${expandedItems['normativas-5'] ? 'transform rotate-180' : ''}`} 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              {expandedItems['normativas-5'] && (
                <div className="p-4 pt-0 border-t border-gray-200">
                  <p className="text-gray-700">
                    O Grupo M7 CORP oferece diversos serviços para auxiliar empresas na adequação às normas regulamentadoras:
                  </p>
                  <ul className="list-disc pl-5 mt-2 space-y-1 text-gray-700">
                    <li>Diagnóstico de conformidade com as normas aplicáveis</li>
                    <li>Elaboração de planos de ação para adequação</li>
                    <li>Inspeções e ensaios conforme requisitos normativos</li>
                    <li>Elaboração e atualização de documentação técnica</li>
                    <li>Treinamentos para operadores e mantenedores</li>
                    <li>Assessoria técnica durante fiscalizações</li>
                    <li>Projetos de engenharia para adequação de equipamentos</li>
                    <li>Elaboração de procedimentos operacionais e de manutenção</li>
                  </ul>
                  <p className="mt-3 text-gray-700">
                    Nossa abordagem é personalizada para cada cliente, considerando suas necessidades específicas, tipo de indústria e equipamentos. Trabalhamos em parceria com nossos clientes para garantir a conformidade legal e a segurança operacional.
                  </p>
                  <p className="mt-2 text-gray-700">
                    Para mais informações sobre nossos serviços de adequação normativa, entre em contato conosco através da <Link to="/contato" className="text-[#4D8BC9] hover:text-[#1A3A5F]">página de contato</Link>.
                  </p>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Perguntas e Respostas - Contratação */}
      <section id="contratacao" className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="mb-8"
          >
            <h2 className="text-2xl font-bold mb-4 text-[#1A3A5F]">Perguntas sobre Contratação</h2>
          </motion.div>

          <div className="space-y-4 max-w-4xl">
            {/* Pergunta 1 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-white rounded-lg shadow-md overflow-hidden"
            >
              <div 
                className="p-4 flex justify-between items-center cursor-pointer"
                onClick={() => toggleItem('contratacao-1')}
              >
                <h3 className="text-lg font-bold text-[#1A3A5F]">Como solicitar uma proposta de serviços?</h3>
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className={`h-6 w-6 text-[#4D8BC9] transition-transform ${expandedItems['contratacao-1'] ? 'transform rotate-180' : ''}`} 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              {expandedItems['contratacao-1'] && (
                <div className="p-4 pt-0 border-t border-gray-200">
                  <p className="text-gray-700">
                    Para solicitar uma proposta de serviços, você pode:
                  </p>
                  <ul className="list-disc pl-5 mt-2 space-y-1 text-gray-700">
                    <li>Preencher o formulário em nossa <Link to="/contato" className="text-[#4D8BC9] hover:text-[#1A3A5F]">página de contato</Link></li>
                    <li>Enviar um e-mail para comercial@m7corp.com.br</li>
                    <li>Ligar para (11) 4444-5555</li>
                    <li>Entrar em contato via WhatsApp: (11) 99999-8888</li>
                  </ul>
                  <p className="mt-3 text-gray-700">
                    Para agilizar o processo, recomendamos fornecer as seguintes informações:
                  </p>
                  <ul className="list-disc pl-5 mt-2 space-y-1 text-gray-700">
                    <li>Tipo de serviço desejado</li>
                    <li>Descrição dos equipamentos (tipo, quantidade, características)</li>
                    <li>Localização da planta/equipamentos</li>
                    <li>Prazo desejado para execução</li>
                    <li>Informações específicas relevantes para o serviço</li>
                  </ul>
                  <p className="mt-3 text-gray-700">
                    Nossa equipe comercial entrará em contato em até 24 horas úteis para entender melhor suas necessidades e elaborar uma proposta personalizada.
                  </p>
                </div>
              )}
            </motion.div>

            {/* Pergunta 2 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-white rounded-lg shadow-md overflow-hidden"
            >
              <div 
                className="p-4 flex justify-between items-center cursor-pointer"
                onClick={() => toggleItem('contratacao-2')}
              >
                <h3 className="text-lg font-bold text-[#1A3A5F]">Qual o prazo médio para execução dos serviços?</h3>
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className={`h-6 w-6 text-[#4D8BC9] transition-transform ${expandedItems['contratacao-2'] ? 'transform rotate-180' : ''}`} 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              {expandedItems['contratacao-2'] && (
                <div className="p-4 pt-0 border-t border-gray-200">
                  <p className="text-gray-700">
                    O prazo para execução dos serviços varia de acordo com o tipo de serviço, complexidade, quantidade de equipamentos e localização. Abaixo, apresentamos prazos médios para referência:
                  </p>
                  <ul className="list-disc pl-5 mt-2 space-y-1 text-gray-700">
                    <li><span className="font-semibold">Inspeção de equipamentos:</span> 1 a 5 dias por equipamento, dependendo do tipo e complexidade</li>
                    <li><span className="font-semibold">Ensaios não destrutivos:</span> 1 a 3 dias, dependendo do tipo e extensão</li>
                    <li><span className="font-semibold">Análise de integridade:</span> 5 a 15 dias, incluindo inspeção, análise e relatório</li>
                    <li><span className="font-semibold">Projetos de adequação:</span> 15 a 45 dias, dependendo da complexidade</li>
                    <li><span className="font-semibold">Treinamentos:</span> 1 a 5 dias, dependendo do conteúdo e carga horária</li>
                  </ul>
                  <p className="mt-3 text-gray-700">
                    Para serviços emergenciais, contamos com equipes de pronto atendimento que podem ser mobilizadas em até 24 horas, dependendo da localização.
                  </p>
                  <p className="mt-2 text-gray-700">
                    O prazo exato será definido na proposta comercial, após análise detalhada das necessidades específicas do cliente.
                  </p>
                </div>
              )}
            </motion.div>

            {/* Pergunta 3 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white rounded-lg shadow-md overflow-hidden"
            >
              <div 
                className="p-4 flex justify-between items-center cursor-pointer"
                onClick={() => toggleItem('contratacao-3')}
              >
                <h3 className="text-lg font-bold text-[#1A3A5F]">O Grupo M7 CORP atende em todo o Brasil?</h3>
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className={`h-6 w-6 text-[#4D8BC9] transition-transform ${expandedItems['contratacao-3'] ? 'transform rotate-180' : ''}`} 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              {expandedItems['contratacao-3'] && (
                <div className="p-4 pt-0 border-t border-gray-200">
                  <p className="text-gray-700">
                    Sim, o Grupo M7 CORP atende em todo o território nacional. Contamos com escritórios estrategicamente localizados nas principais regiões industriais do país e equipes móveis que podem ser deslocadas para qualquer localidade.
                  </p>
                  <p className="mt-2 text-gray-700">
                    Nossos principais escritórios estão localizados em:
                  </p>
                  <ul className="list-disc pl-5 mt-2 space-y-1 text-gray-700">
                    <li>São Paulo (Matriz)</li>
                    <li>Rio de Janeiro</li>
                    <li>Belo Horizonte</li>
                    <li>Salvador</li>
                    <li>Recife</li>
                    <li>Porto Alegre</li>
                  </ul>
                  <p className="mt-3 text-gray-700">
                    Além disso, temos parcerias estratégicas com empresas locais em diversas regiões, o que nos permite oferecer atendimento ágil e eficiente em todo o país.
                  </p>
                  <p className="mt-2 text-gray-700">
                    Para serviços internacionais, avaliamos caso a caso, dependendo da localização e tipo de serviço. Entre em contato conosco para verificar a disponibilidade para sua região específica.
                  </p>
                </div>
              )}
            </motion.div>

            {/* Pergunta 4 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
              className="bg-white rounded-lg shadow-md overflow-hidden"
            >
              <div 
                className="p-4 flex justify-between items-center cursor-pointer"
                onClick={() => toggleItem('contratacao-4')}
              >
                <h3 className="text-lg font-bold text-[#1A3A5F]">Quais são as formas de pagamento aceitas?</h3>
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className={`h-6 w-6 text-[#4D8BC9] transition-transform ${expandedItems['contratacao-4'] ? 'transform rotate-180' : ''}`} 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              {expandedItems['contratacao-4'] && (
                <div className="p-4 pt-0 border-t border-gray-200">
                  <p className="text-gray-700">
                    O Grupo M7 CORP oferece diversas formas de pagamento para facilitar a contratação de nossos serviços:
                  </p>
                  <ul className="list-disc pl-5 mt-2 space-y-1 text-gray-700">
                    <li>Boleto bancário</li>
                    <li>Transferência bancária (PIX, TED, DOC)</li>
                    <li>Cartão de crédito corporativo (para serviços de menor valor)</li>
                    <li>Parcelamento em até 3 vezes sem juros (para serviços específicos)</li>
                  </ul>
                  <p className="mt-3 text-gray-700">
                    Para contratos de maior valor ou de longa duração, oferecemos condições especiais de pagamento, como:
                  </p>
                  <ul className="list-disc pl-5 mt-2 space-y-1 text-gray-700">
                    <li>Pagamento por medição (mensal ou por etapas)</li>
                    <li>Parcelamento personalizado</li>
                    <li>Contratos de manutenção com pagamento mensal</li>
                  </ul>
                  <p className="mt-3 text-gray-700">
                    As condições de pagamento são definidas na proposta comercial, de acordo com o tipo de serviço, valor e prazo de execução. Estamos abertos a negociar condições que melhor atendam às necessidades de nossos clientes.
                  </p>
                </div>
              )}
            </motion.div>

            {/* Pergunta 5 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              viewport={{ once: true }}
              className="bg-white rounded-lg shadow-md overflow-hidden"
            >
              <div 
                className="p-4 flex justify-between items-center cursor-pointer"
                onClick={() => toggleItem('contratacao-5')}
              >
                <h3 className="text-lg font-bold text-[#1A3A5F]">O Grupo M7 CORP oferece contratos de manutenção?</h3>
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className={`h-6 w-6 text-[#4D8BC9] transition-transform ${expandedItems['contratacao-5'] ? 'transform rotate-180' : ''}`} 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              {expandedItems['contratacao-5'] && (
                <div className="p-4 pt-0 border-t border-gray-200">
                  <p className="text-gray-700">
                    Sim, o Grupo M7 CORP oferece contratos de manutenção e inspeção periódica para diversos tipos de equipamentos industriais. Nossos contratos são personalizados de acordo com as necessidades específicas de cada cliente e podem incluir:
                  </p>
                  <ul className="list-disc pl-5 mt-2 space-y-1 text-gray-700">
                    <li>Inspeções periódicas conforme requisitos normativos</li>
                    <li>Ensaios não destrutivos programados</li>
                    <li>Análise de integridade e vida remanescente</li>
                    <li>Manutenção preventiva e preditiva</li>
                    <li>Atendimento emergencial</li>
                    <li>Gestão da documentação técnica</li>
                    <li>Treinamentos periódicos para operadores</li>
                  </ul>
                  <p className="mt-3 text-gray-700">
                    Os contratos de manutenção oferecem diversas vantagens:
                  </p>
                  <ul className="list-disc pl-5 mt-2 space-y-1 text-gray-700">
                    <li>Previsibilidade de custos</li>
                    <li>Prioridade no atendimento</li>
                    <li>Descontos em serviços adicionais</li>
                    <li>Garantia de conformidade normativa</li>
                    <li>Redução de paradas não programadas</li>
                    <li>Aumento da vida útil dos equipamentos</li>
                  </ul>
                  <p className="mt-3 text-gray-700">
                    Para mais informações sobre nossos contratos de manutenção, entre em contato conosco através da <Link to="/contato" className="text-[#4D8BC9] hover:text-[#1A3A5F]">página de contato</Link>.
                  </p>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Perguntas e Respostas - Contato */}
      <section id="contato" className="py-12">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="mb-8"
          >
            <h2 className="text-2xl font-bold mb-4 text-[#1A3A5F]">Perguntas sobre Contato</h2>
          </motion.div>

          <div className="space-y-4 max-w-4xl">
            {/* Pergunta 1 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-white rounded-lg shadow-md overflow-hidden"
            >
              <div 
                className="p-4 flex justify-between items-center cursor-pointer"
                onClick={() => toggleItem('contato-1')}
              >
                <h3 className="text-lg font-bold text-[#1A3A5F]">Quais são os canais de atendimento do Grupo M7 CORP?</h3>
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className={`h-6 w-6 text-[#4D8BC9] transition-transform ${expandedItems['contato-1'] ? 'transform rotate-180' : ''}`} 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              {expandedItems['contato-1'] && (
                <div className="p-4 pt-0 border-t border-gray-200">
                  <p className="text-gray-700">
                    O Grupo M7 CORP disponibiliza diversos canais de atendimento para facilitar o contato com nossos clientes:
                  </p>
                  <ul className="list-disc pl-5 mt-2 space-y-1 text-gray-700">
                    <li><span className="font-semibold">Telefone:</span> (11) 4444-5555 (Segunda a Sexta, das 8h às 18h)</li>
                    <li><span className="font-semibold">WhatsApp:</span> (11) 99999-8888 (Atendimento 24/7 para emergências)</li>
                    <li><span className="font-semibold">E-mail:</span> contato@m7corp.com.br</li>
                    <li><span className="font-semibold">Formulário de Contato:</span> Disponível em nossa <Link to="/contato" className="text-[#4D8BC9] hover:text-[#1A3A5F]">página de contato</Link></li>
                    <li><span className="font-semibold">LinkedIn:</span> <a href="https://www.linkedin.com/company/m7corp" target="_blank" rel="noopener noreferrer" className="text-[#4D8BC9] hover:text-[#1A3A5F]">linkedin.com/company/m7corp</a></li>
                  </ul>
                  <p className="mt-3 text-gray-700">
                    Para atendimento comercial específico:
                  </p>
                  <ul className="list-disc pl-5 mt-2 space-y-1 text-gray-700">
                    <li><span className="font-semibold">Telefone:</span> (11) 4444-5556</li>
                    <li><span className="font-semibold">E-mail:</span> comercial@m7corp.com.br</li>
                  </ul>
                  <p className="mt-3 text-gray-700">
                    Para suporte técnico:
                  </p>
                  <ul className="list-disc pl-5 mt-2 space-y-1 text-gray-700">
                    <li><span className="font-semibold">Telefone:</span> (11) 4444-5557</li>
                    <li><span className="font-semibold">E-mail:</span> suporte@m7corp.com.br</li>
                  </ul>
                </div>
              )}
            </motion.div>

            {/* Pergunta 2 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-white rounded-lg shadow-md overflow-hidden"
            >
              <div 
                className="p-4 flex justify-between items-center cursor-pointer"
                onClick={() => toggleItem('contato-2')}
              >
                <h3 className="text-lg font-bold text-[#1A3A5F]">Onde estão localizados os escritórios do Grupo M7 CORP?</h3>
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className={`h-6 w-6 text-[#4D8BC9] transition-transform ${expandedItems['contato-2'] ? 'transform rotate-180' : ''}`} 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              {expandedItems['contato-2'] && (
                <div className="p-4 pt-0 border-t border-gray-200">
                  <p className="text-gray-700">
                    O Grupo M7 CORP possui escritórios estrategicamente localizados nas principais regiões industriais do Brasil:
                  </p>
                  <p className="mt-2 font-semibold text-gray-700">Matriz (São Paulo):</p>
                  <p className="text-gray-700">
                    Av. Paulista, 1000, 10º andar<br />
                    Bela Vista, São Paulo - SP<br />
                    CEP: 01310-100<br />
                    Telefone: (11) 4444-5555
                  </p>
                  <p className="mt-2 font-semibold text-gray-700">Filial Rio de Janeiro:</p>
                  <p className="text-gray-700">
                    Av. Rio Branco, 500, Sala 1001<br />
                    Centro, Rio de Janeiro - RJ<br />
                    CEP: 20040-002<br />
                    Telefone: (21) 3333-4444
                  </p>
                  <p className="mt-2 font-semibold text-gray-700">Filial Belo Horizonte:</p>
                  <p className="text-gray-700">
                    Av. Afonso Pena, 300, Sala 501<br />
                    Centro, Belo Horizonte - MG<br />
                    CEP: 30130-001<br />
                    Telefone: (31) 2222-3333
                  </p>
                  <p className="mt-3 text-gray-700">
                    Além desses escritórios principais, contamos com unidades operacionais em Salvador, Recife e Porto Alegre, além de parcerias estratégicas em diversas regiões do país.
                  </p>
                  <p className="mt-2 text-gray-700">
                    Para mais informações sobre nossos escritórios e como chegar, visite nossa <Link to="/contato" className="text-[#4D8BC9] hover:text-[#1A3A5F]">página de contato</Link>.
                  </p>
                </div>
              )}
            </motion.div>

            {/* Pergunta 3 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white rounded-lg shadow-md overflow-hidden"
            >
              <div 
                className="p-4 flex justify-between items-center cursor-pointer"
                onClick={() => toggleItem('contato-3')}
              >
                <h3 className="text-lg font-bold text-[#1A3A5F]">Qual o horário de atendimento do Grupo M7 CORP?</h3>
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className={`h-6 w-6 text-[#4D8BC9] transition-transform ${expandedItems['contato-3'] ? 'transform rotate-180' : ''}`} 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              {expandedItems['contato-3'] && (
                <div className="p-4 pt-0 border-t border-gray-200">
                  <p className="text-gray-700">
                    Nosso horário de atendimento varia de acordo com o departamento e tipo de serviço:
                  </p>
                  <p className="mt-2 font-semibold text-gray-700">Atendimento Comercial:</p>
                  <p className="text-gray-700">
                    Segunda a Sexta-feira, das 8h às 18h
                  </p>
                  <p className="mt-2 font-semibold text-gray-700">Suporte Técnico:</p>
                  <p className="text-gray-700">
                    Segunda a Sexta-feira, das 8h às 18h<br />
                    Sábados, das 8h às 12h (apenas para clientes com contrato)
                  </p>
                  <p className="mt-2 font-semibold text-gray-700">Atendimento de Emergência:</p>
                  <p className="text-gray-700">
                    24 horas por dia, 7 dias por semana (para clientes com contrato de atendimento emergencial)
                  </p>
                  <p className="mt-3 text-gray-700">
                    Para atendimento fora do horário comercial, disponibilizamos um número de telefone exclusivo para emergências: (11) 99999-7777.
                  </p>
                  <p className="mt-2 text-gray-700">
                    Mensagens enviadas por e-mail ou pelo formulário de contato fora do horário comercial serão respondidas no próximo dia útil.
                  </p>
                </div>
              )}
            </motion.div>

            {/* Pergunta 4 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
              className="bg-white rounded-lg shadow-md overflow-hidden"
            >
              <div 
                className="p-4 flex justify-between items-center cursor-pointer"
                onClick={() => toggleItem('contato-4')}
              >
                <h3 className="text-lg font-bold text-[#1A3A5F]">Como posso acompanhar o andamento dos serviços contratados?</h3>
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className={`h-6 w-6 text-[#4D8BC9] transition-transform ${expandedItems['contato-4'] ? 'transform rotate-180' : ''}`} 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              {expandedItems['contato-4'] && (
                <div className="p-4 pt-0 border-t border-gray-200">
                  <p className="text-gray-700">
                    O Grupo M7 CORP oferece diversas formas de acompanhamento dos serviços contratados:
                  </p>
                  <ul className="list-disc pl-5 mt-2 space-y-1 text-gray-700">
                    <li><span className="font-semibold">Portal do Cliente:</span> Acesse nossa <Link to="/cliente-area" className="text-[#4D8BC9] hover:text-[#1A3A5F]">Área do Cliente</Link> com seu login e senha para acompanhar o status dos serviços, visualizar relatórios parciais e acessar documentos.</li>
                    <li><span className="font-semibold">Relatórios de Progresso:</span> Para projetos de longa duração, enviamos relatórios periódicos de progresso por e-mail.</li>
                    <li><span className="font-semibold">Contato Direto:</span> Seu gerente de projeto está disponível para fornecer atualizações por telefone ou e-mail.</li>
                    <li><span className="font-semibold">Reuniões de Acompanhamento:</span> Para projetos complexos, realizamos reuniões periódicas de acompanhamento, presenciais ou virtuais.</li>
                  </ul>
                  <p className="mt-3 text-gray-700">
                    Caso tenha dúvidas sobre o andamento de um serviço específico, você pode entrar em contato com nosso suporte técnico pelos seguintes canais:
                  </p>
                  <ul className="list-disc pl-5 mt-2 space-y-1 text-gray-700">
                    <li>Telefone: (11) 4444-5557</li>
                    <li>E-mail: suporte@m7corp.com.br</li>
                    <li>WhatsApp: (11) 99999-6666</li>
                  </ul>
                  <p className="mt-3 text-gray-700">
                    Para acessar o Portal do Cliente pela primeira vez, solicite suas credenciais através do e-mail suporte@m7corp.com.br.
                  </p>
                </div>
              )}
            </motion.div>

            {/* Pergunta 5 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              viewport={{ once: true }}
              className="bg-white rounded-lg shadow-md overflow-hidden"
            >
              <div 
                className="p-4 flex justify-between items-center cursor-pointer"
                onClick={() => toggleItem('contato-5')}
              >
                <h3 className="text-lg font-bold text-[#1A3A5F]">Como posso enviar documentos técnicos para análise?</h3>
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className={`h-6 w-6 text-[#4D8BC9] transition-transform ${expandedItems['contato-5'] ? 'transform rotate-180' : ''}`} 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              {expandedItems['contato-5'] && (
                <div className="p-4 pt-0 border-t border-gray-200">
                  <p className="text-gray-700">
                    Você pode enviar documentos técnicos para análise através dos seguintes canais:
                  </p>
                  <ul className="list-disc pl-5 mt-2 space-y-1 text-gray-700">
                    <li><span className="font-semibold">Portal do Cliente:</span> Acesse nossa <Link to="/cliente-area" className="text-[#4D8BC9] hover:text-[#1A3A5F]">Área do Cliente</Link> e utilize a função de upload de documentos.</li>
                    <li><span className="font-semibold">E-mail:</span> Envie documentos para tecnico@m7corp.com.br (limite de 25MB por e-mail).</li>
                    <li><span className="font-semibold">Serviços de Compartilhamento:</span> Para arquivos maiores, utilize serviços como Google Drive, OneDrive ou WeTransfer e compartilhe o link através do e-mail tecnico@m7corp.com.br.</li>
                    <li><span className="font-semibold">Entrega Física:</span> Para documentos físicos ou mídias digitais, entregue em um de nossos escritórios ou solicite a coleta por um de nossos representantes.</li>
                  </ul>
                  <p className="mt-3 text-gray-700">
                    Recomendações para o envio de documentos técnicos:
                  </p>
                  <ul className="list-disc pl-5 mt-2 space-y-1 text-gray-700">
                    <li>Identifique claramente o projeto ou serviço relacionado aos documentos.</li>
                    <li>Organize os arquivos em pastas lógicas e com nomes descritivos.</li>
                    <li>Para desenhos técnicos, prefira formatos editáveis (DWG, DXF) além dos formatos de visualização (PDF).</li>
                    <li>Inclua uma lista dos documentos enviados e suas revisões.</li>
                    <li>Para documentos confidenciais, utilize nossa plataforma segura de compartilhamento (solicite acesso ao seu gerente de projeto).</li>
                  </ul>
                  <p className="mt-3 text-gray-700">
                    Após o recebimento, nossa equipe técnica confirmará o recebimento e informará o prazo para análise dos documentos.
                  </p>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Não Encontrou sua Pergunta */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4 text-[#1A3A5F]">Não Encontrou sua Pergunta?</h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              Se você não encontrou a resposta que procura, entre em contato conosco. 
              Nossa equipe está pronta para esclarecer suas dúvidas e fornecer as informações necessárias.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <div className="bg-white p-8 rounded-lg shadow-md">
              <form className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-gray-700 font-medium mb-2">Nome</label>
                    <input 
                      type="text" 
                      id="name" 
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#4D8BC9]" 
                      placeholder="Seu nome"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-gray-700 font-medium mb-2">E-mail</label>
                    <input 
                      type="email" 
                      id="email" 
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#4D8BC9]" 
                      placeholder="seu.email@exemplo.com"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="subject" className="block text-gray-700 font-medium mb-2">Assunto</label>
                  <input 
                    type="text" 
                    id="subject" 
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#4D8BC9]" 
                    placeholder="Assunto da sua pergunta"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-gray-700 font-medium mb-2">Sua Pergunta</label>
                  <textarea 
                    id="message" 
                    rows={5}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#4D8BC9]" 
                    placeholder="Descreva sua pergunta em detalhes..."
                  ></textarea>
                </div>
                <div className="text-center">
                  <button 
                    type="submit" 
                    className="bg-[#1A3A5F] hover:bg-[#4D8BC9] text-white font-bold px-8 py-3 rounded-md transition-colors"
                  >
                    Enviar Pergunta
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#1A3A5F] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-6">Precisa de Ajuda Especializada?</h2>
            <p className="text-xl mb-8">
              Nossa equipe de especialistas está pronta para ajudar com suas necessidades específicas. 
              Entre em contato conosco para uma consulta personalizada.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link 
                to="/contato" 
                className="bg-white text-[#1A3A5F] hover:bg-gray-100 font-bold px-8 py-3 rounded-md transition-colors"
              >
                Fale Conosco
              </Link>
              <Link 
                to="/servicos" 
                className="bg-transparent border-2 border-white hover:bg-white hover:text-[#1A3A5F] text-white font-bold px-8 py-3 rounded-md transition-colors"
              >
                Nossos Serviços
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;
